import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { SignInProviders, oauthErrorMessage } from "../components/SignInProviders";
import { ApiError, api } from "../lib/api";
import { useSession } from "../lib/session";

const MIN_PASSWORD = 12;

export default function Register() {
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") ?? "";
  const invitedEmail = params.get("email") ?? "";
  const { applyStep, settings } = useSession();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(() => oauthErrorMessage(params.get("oauth_error")));
  const [busy, setBusy] = useState(false);

  const tooShortBy = useMemo(() => Math.max(0, MIN_PASSWORD - password.length), [password]);
  const closed = !settings.allowSignup && !inviteToken;
  const providers = settings.signInProviders ?? [];

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.register({
        email,
        password,
        displayName,
        ...(inviteToken ? { inviteToken } : {}),
      });
      applyStep(result.step, false);
      navigate("/mfa/set-up", { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Your account could not be created.");
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      heading="Create your account"
      blurb={
        inviteToken
          ? "You have been invited. Use the email address the invitation was sent to."
          : "Registration is open. You will set up two-factor authentication next — it is required."
      }
    >
      {closed ? (
        <>
          <p className="form-error" role="alert">
            This course is invitation only. Ask an administrator for an invitation link, then open
            it to create your account.
          </p>
          <div className="auth-alt">
            <Link to="/sign-in">Back to sign in</Link>
          </div>
        </>
      ) : (
        <>
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}

          {/* Above both ways in, so a provider sign-up sees it before leaving for the provider. */}
          <p className="hint">
            By creating an account you agree to the{" "}
            <a href="https://terraformed.dev/terms" target="_blank" rel="noopener">terms</a> and the{" "}
            <a href="https://terraformed.dev/covenant" target="_blank" rel="noopener">Learners' Covenant</a>,
            and you have read the{" "}
            <a href="https://terraformed.dev/privacy" target="_blank" rel="noopener">privacy notice</a>.
          </p>

          {providers.length ? (
            <>
              {/* With an invitation, the account takes the invited address whichever provider
                  is used; without one, the provider has to have verified the address. */}
              <SignInProviders
                providers={providers}
                verb="Sign up with"
                query={inviteToken ? { invite: inviteToken, email: invitedEmail || undefined } : {}}
              />
              <p className="auth-or">or create a password</p>
            </>
          ) : null}

          <form className="stack" onSubmit={onSubmit} noValidate>

            <div className="field">
              <label htmlFor="name">Your name</label>
              <input
                id="name"
                className="input"
                name="name"
                autoComplete="name"
                autoFocus
                required
                minLength={2}
                maxLength={80}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <p className="hint">This is the name that goes on your certificate.</p>
            </div>

            <div className="field">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                className="input"
                type="email"
                name="email"
                autoComplete="email"
                required
                readOnly={!!invitedEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {invitedEmail ? (
                <p className="hint">Fixed by your invitation.</p>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                className="input"
                type="password"
                name="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby="password-help"
              />
              <p className="hint" id="password-help">
                {tooShortBy > 0
                  ? `At least ${MIN_PASSWORD} characters — ${tooShortBy} to go.`
                  : "Long enough. A passphrase of a few unrelated words works well."}
              </p>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? <span className="spinner" /> : null}
              {busy ? "Creating your account…" : "Create account"}
            </button>
          </form>

          <div className="auth-alt">
            <Link to="/sign-in">I already have an account</Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
